import { ListItemIcon, ListItemText, Stack } from "@mui/material";
import { AxiosResponse } from "axios";
import { ReactNode } from "react";

import { ROUTE_PATH } from "@/common/constants/routing";
import { AppPermission, RepairSparePartDto } from "@/core/api/generated/v1";

import AuthorizedMenuItem from "../../Auth/AuthorizedMenuItem";
import EntityMenu from "../../EntityMenu/EntityMenu";
import AppIcon from "../../Icons/AppIcon";
import AppLink from "../../Link/AppLink";
import DeleteRepairSparePartModal from "./DeleteRepairSparePartModal";

enum ActionFlags {
  Edit = "Edit",
  Delete = "Delete",
}
const defaultDisplayProps = {
  actions: {
    edit: true,
    delete: true,
  },
};

interface TriggersProps {
  entity?: RepairSparePartDto | null;
  definition: typeof ActionFlags;
  handleClose: () => void;
  startAction: (props: ActionFlags) => void;
}

interface Props {
  entity?: RepairSparePartDto | null;
  disabled?: boolean;
  onUpdate: ((newValue: RepairSparePartDto | null | undefined) => void) | undefined;
  onDelete?: (() => void) | undefined;
  displayProps?: Partial<typeof defaultDisplayProps>;
  deleteFunc?: (params: { repairSparePartId: string }) => Promise<AxiosResponse<unknown, unknown>>;
  customActions?: (props: TriggersProps) => ReactNode;
}

export default function RepairSparePartMenu({
  entity,
  disabled,
  customActions,
  deleteFunc,
  onUpdate,
  onDelete,
  displayProps = defaultDisplayProps,
}: Props) {
  displayProps = { ...defaultDisplayProps, ...displayProps };
  return (
    <EntityMenu
      definition={ActionFlags}
      entity={entity}
      disabled={disabled}
      actionTriggers={({ handleClose, startAction }) => (
        <>
          {customActions &&
            customActions({
              entity,
              definition: ActionFlags,
              handleClose,
              startAction,
            })}
          {displayProps?.actions?.edit && (
            <AuthorizedMenuItem
              permissions={[AppPermission.RepairCatalogManage]}
              component={AppLink}
              to={ROUTE_PATH.REPAIR_SPARE_PART_EDIT(entity?.id)}
            >
              <ListItemIcon>
                <AppIcon of='edit' fontSize='small' />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </AuthorizedMenuItem>
          )}
          {displayProps?.actions?.delete && (
            <AuthorizedMenuItem
              permissions={[AppPermission.RepairCatalogManage]}
              onClick={() => startAction(ActionFlags.Delete)}
            >
              <ListItemIcon>
                <AppIcon of='delete' fontSize='small' />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </AuthorizedMenuItem>
          )}
        </>
      )}
      actionHandlers={({ currentAction, cancelAction, completeAction }) => (
        <Stack>
          {/* Delete entity */}
          {entity && (
            <DeleteRepairSparePartModal
              open={currentAction === ActionFlags.Delete}
              onClose={() => cancelAction()}
              deleteFunc={deleteFunc}
              onDelete={() => {
                onDelete && onDelete();
                completeAction();
              }}
              entity={entity}
            />
          )}
        </Stack>
      )}
    />
  );
}
